// Rental — listing table. Filters: store, floor, payment-date range. Row menu:
// View / Edit / Delete.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AppConstant from "../../Globals/AppConstants";
import { FLOOR_OPTIONS, FinancialYear, MONTH_OPTIONS, financialYearLabel, floorLabel, inr, monthLabel } from "./rentalConstants";

const ALL = "all";

export interface RentalTableProps {
    data: any[];
    loading: boolean;
    onView: (item: any) => void;
    onEdit: (item: any) => void;
    onDelete: (item: any) => void;
    onAttachment: (item: any) => void;
    storeOptions: { value: string; label: string }[];
    storeFilter?: string;
    onStoreFilterChange: (storeId: string) => void;
    floorFilter?: string;
    onFloorFilterChange: (floor: string) => void;
    monthFilter?: string;
    onMonthFilterChange: (month: string) => void;
    financialYears: FinancialYear[];
    /** Selected financial year label ("2026-2027"); empty = all years. */
    yearFilter?: string;
    onYearFilterChange: (label: string) => void;
    title?: React.ReactNode;
    headerActions?: React.ReactNode;
    pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function RentalTable({
    data,
    loading,
    onView,
    onEdit,
    onDelete,
    onAttachment,
    storeOptions,
    storeFilter,
    onStoreFilterChange,
    floorFilter,
    onFloorFilterChange,
    monthFilter,
    onMonthFilterChange,
    financialYears,
    yearFilter,
    onYearFilterChange,
    title,
    headerActions,
    pagination,
}: RentalTableProps) {
    const columns = React.useMemo<ColumnDef<any, any>[]>(
        () => [
            {
                id: "rentalCode",
                header: "Rental ID",
                cell: ({ row }) => row.original?.rentalCode ?? "-",
                meta: { sticky: "left" },
            },
            {
                id: "storeName",
                header: "Store",
                cell: ({ row }) => (
                    <div>
                        <span className="block">{row.original?.storeName}</span>
                        {row.original?.storeLocation && row.original.storeLocation !== row.original?.storeName && (
                            <span className="block text-xs text-muted-foreground">{row.original.storeLocation}</span>
                        )}
                    </div>
                ),
            },
            { id: "floor", header: "Floor", cell: ({ row }) => floorLabel(row.original?.floor) },
            { id: "rentMonth", header: "Month", cell: ({ row }) => monthLabel(row.original?.rentMonth) },
            { id: "financialYear", header: "Year", cell: ({ row }) => financialYearLabel(row.original?.financialYear) },
            {
                id: "paymentDate",
                header: "Date of Payment",
                cell: ({ row }) => (row.original?.paymentDate ? moment(row.original.paymentDate).format("DD-MMM-YYYY") : "-"),
            },
            {
                id: "amount",
                header: "Amount",
                cell: ({ row }) => <span className="tabular-nums font-medium">{inr(row.original?.amount)}</span>,
            },
            {
                id: "attachment",
                header: "Attachment",
                cell: ({ row }) => row.original?.attachmentFileUuid ? (
                    <button
                        type="button"
                        onClick={() => onAttachment(row.original)}
                        className="text-primary hover:underline"
                    >
                        {AppConstant?.view}
                    </button>
                ) : "-",
            },
            {
                id: "remarks",
                header: "Remarks",
                cell: ({ row }) => (
                    <span className="block max-w-[220px] truncate" title={row.original?.remarks || ""}>
                        {row.original?.remarks || "-"}
                    </span>
                ),
            },
            {
                id: "createdOn",
                header: "Created On",
                cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY, hh:mm A") : "-"),
            },
            { id: "createdByName", header: "Created By", cell: ({ row }) => row.original?.createdByName ?? "-" },
            {
                id: "action",
                header: "Action",
                cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Row actions">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onView(item)}>{AppConstant?.view}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onEdit(item)}>{AppConstant?.edit}</DropdownMenuItem>
                                <DropdownMenuItem className="text-destructive" onClick={() => onDelete(item)}>Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                meta: { sticky: "right" },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const filterToolbar = (
        <div className="flex flex-wrap items-center gap-2">
            <Select value={storeFilter || ALL} onValueChange={(v) => onStoreFilterChange(v === ALL ? "" : v)}>
                <SelectTrigger className="w-[190px]"><SelectValue placeholder="All Stores" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL}>All Stores</SelectItem>
                    {storeOptions.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select value={floorFilter || ALL} onValueChange={(v) => onFloorFilterChange(v === ALL ? "" : v)}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Floors" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL}>All Floors</SelectItem>
                    {FLOOR_OPTIONS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select value={monthFilter || ALL} onValueChange={(v) => onMonthFilterChange(v === ALL ? "" : v)}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Months" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL}>All Months</SelectItem>
                    {MONTH_OPTIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select value={yearFilter || ALL} onValueChange={(v) => onYearFilterChange(v === ALL ? "" : v)}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL}>All Years</SelectItem>
                    {financialYears.map((fy) => (
                        <SelectItem key={fy.label} value={fy.label}>{fy.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    return (
        <DataTable
            columns={columns}
            data={data || []}
            loading={loading}
            toolbar={filterToolbar}
            title={title}
            headerActions={headerActions}
            pagination={pagination}
        />
    );
}

export default RentalTable;
